<template>
    <section
        v-if="slice.primary"
        :id="slice.primary.anchorName || undefined"
        ref="section"
        class="slice slice-profiles slice-profiles--with-shapes"
        aria-label="profiles"
        data-testid="slice-profiles-with-shapes"
    >
        <div class="slice-content">
            <PrismicRichText
                v-if="slice.primary.title"
                :field="slice.primary.title"
                class="slice-title"
                data-testid="slice-profiles-with-shapes-title"
            />
            <PrismicRichText
                v-if="slice.primary.subtitle"
                :field="slice.primary.subtitle"
                class="slice-subtitle"
                data-testid="slice-profiles-with-shapes-subtitle"
            />
            <PrismicRichText
                v-if="slice.primary.description"
                :field="slice.primary.description"
                class="slice-description"
                data-testid="slice-profiles-with-shapes-description"
            />
            <div v-if="slice.primary.profiles.length > 1" class="slice-body" data-testid="slice-profiles-with-shapes-carousel">
                <Swiper class="swiper-grid" :breakpoints="swiperBreakpoints">
                    <SwiperSlide v-for="(profile, i) in slice.primary.profiles" :key="i">
                        <WithShapesCard :profile="profile" :theme="slice.primary.theme" :index="i" />
                    </SwiperSlide>
                </Swiper>
            </div>
            <div v-else class="slice-profiles--with-shapes__single" data-testid="slice-profiles-with-shapes-single">
                <MktCardHand :ms-theme="slice.primary.theme === 'strategy'">
                    <PrismicImage
                        v-if="slice.primary.profiles[0]?.image"
                        :field="slice.primary.profiles[0]?.image"
                        data-testid="slice-profiles-with-shapes-single-image"
                    />
                </MktCardHand>
                <div>
                    <div class="slice-profiles--with-shapes__single-name" data-testid="slice-profiles-with-shapes-single-name">
                        {{ profileNameAndJobTitle }}
                    </div>
                    <PrismicRichText
                        :field="description"
                        class="slice-profiles--with-shapes__single-job"
                        data-testid="slice-profiles-with-shapes-single-job"
                    />
                </div>
            </div>

            <div v-if="isFilled.link(slice.primary.cta)" class="slice-footer">
                <VJoyButton
                    :href="slice.primary.cta.url ? $withLocalePrefix(slice.primary.cta.url) : undefined"
                    data-component-source="consultant-profiles"
                    data-testid="slice-profiles-with-shapes-cta"
                >
                    {{ slice.primary.cta.text }}
                </VJoyButton>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {computed, useTemplateRef} from 'vue';
    import {isFilled} from '@prismicio/helpers';
    import {getSliceComponentProps, PrismicImage} from '@prismicio/vue';
    import {PrismicRichText} from '@prismicio/vue';
    import type {ProfilesSlice} from '@prismicio-types';
    import {Swiper, SwiperSlide} from 'swiper/vue';
    import {MktCardHand} from '#components';
    import {useComponentTracking} from '#imports';
    import {VJoyButton} from '@maltjoy/core-vue';
    import WithShapesCard from '../components/WithShapesCard.vue';

    type ProfileWithShapesVariation = Extract<ProfilesSlice, {variation: 'profilesWithShapes'}>;

    const props = defineProps(getSliceComponentProps<ProfileWithShapesVariation>());

    const section = useTemplateRef('section');

    const name = computed(() => props.slice.primary.profiles[0]?.name);
    const description = computed(() => props.slice.primary.profiles[0]?.text1);
    const profileNameAndJobTitle = computed(() => `${name.value} - ${props.slice.primary.profiles[0]?.job}`);

    const swiperBreakpoints = {
        0: {slidesPerView: 1},
        768: {slidesPerView: 2},
        1024: {slidesPerView: 3},
    };

    useComponentTracking(section);
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .slice-profiles--with-shapes {
        &__single {
            flex-direction: column;
            text-align: center;
            margin-top: var(--joy-core-spacing-10);

            @include mq.screen_sm {
                padding: 0 var(--joy-core-spacing-6);
            }

            @include mq.screen_lg(min) {
                width: 100%;
                display: flex;
                text-align: left;
                gap: var(--joy-core-spacing-12);
                flex-direction: row;
                align-items: center;
                justify-content: center;
            }

            &-name {
                color: var(--joy-color-text-heading);
                font-size: var(--joy-font-size-secondary-200);
                font-weight: var(--joy-font-weight-bold);
                position: relative;

                &,
                strong {
                    font-family: var(--joy-font-family-title);
                    font-weight: var(--joy-font-weight-normal);
                }
            }

            &-job {
                margin-top: var(--joy-core-spacing-2);
                color: var(--joy-color-neutral-40);
                font-size: 18px;
            }
        }

        .swiper-grid {
            .swiper-slide {
                width: auto;
            }

            @include mq.screen_lg(min) {
                pointer-events: none;

                .swiper-wrapper {
                    flex-wrap: wrap;
                    justify-content: center;
                }
            }
        }
    }
</style>
